import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Printer, ArrowLeft } from "lucide-react";
import { fetchCountries } from "@/lib/api";
import { fmtNumber } from "@/lib/i18n";
import { useLang } from "@/context/LanguageContext";
import { trackEvent } from "@/lib/analytics";

const pick = (obj, lang) => (obj && (obj[lang] || obj.en)) || "";
const imgSrc = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export default function PrintGuide() {
  const { code } = useParams();
  const { lang, t } = useLang();
  const { data: countries = [] } = useQuery({ queryKey: ["countries"], queryFn: fetchCountries });
  const country = countries.find((c) => c.code === code?.toUpperCase());

  useEffect(() => {
    if (country) document.title = `${country.names[lang] || country.names.en} — ${t("guide")}`;
    return () => { document.title = "Atlante del Mondo Arabo | أطلس العالم العربي"; };
  }, [country, lang, t]);

  if (!country) return <div className="max-w-3xl mx-auto px-4 py-16 text-center text-muted-foreground">…</div>;
  const g = country.travel_guide;
  const name = country.names[lang] || country.names.en;

  const onPrint = () => { trackEvent("print_guide", { country_code: country.code, lang }); window.print(); };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-8 print:py-0 print:px-0 print:max-w-none" data-testid="print-guide">
      <div className="flex items-center justify-between mb-6 print:hidden">
        <Link to={`/?c=${country.code}`} className="inline-flex items-center gap-1.5 text-sm text-ochre hover:underline" data-testid="print-guide-back">
          <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t("back")}
        </Link>
        <button onClick={onPrint} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ink text-sand text-sm hover:bg-ochre transition-colors" data-testid="print-guide-button">
          <Printer className="w-4 h-4" /> {t("print_pdf")}
        </button>
      </div>

      <header className="flex items-center gap-5 border-b-2 border-ochre pb-5 mb-6">
        <img src={country.flag_url} alt="" className="w-28 h-[76px] object-cover rounded-md border border-stoneLine shadow-sm" />
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-ochre font-semibold">{t("app_title")} · {t("guide")}</div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold text-ink leading-tight" data-testid="print-guide-title">{name}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("capital")}: {country.capital[lang]} · {t("population")}: {fmtNumber(country.population, lang)} · {t("currency")}: {country.currency[lang]}
          </p>
        </div>
      </header>

      <p className="text-sm leading-relaxed text-ink/85 mb-6">{country.intro[lang]}</p>

      {g && (
        <div className="space-y-6">
          <Block title={t("guide_places")}>
            <div className="grid grid-cols-2 gap-3">
              {g.places.map((p) => (
                <div key={p.wiki} className="rounded-lg overflow-hidden border border-stoneLine break-inside-avoid">
                  {p.image && <img src={imgSrc(p.image)} alt="" className="w-full h-32 object-cover" />}
                  <div className="p-2.5">
                    <div className="text-sm font-semibold text-ink">{pick(p.name, lang)}</div>
                    <p className="text-xs text-muted-foreground mt-0.5">{pick(p.desc, lang)}</p>
                  </div>
                </div>
              ))}
            </div>
          </Block>

          <Block title={t("guide_itinerary")}>
            <ol className="space-y-1.5">
              {g.itinerary.map((d, i) => (
                <li key={d.en} className="flex gap-3 text-sm">
                  <span className="w-5 h-5 shrink-0 rounded-full bg-ochre text-white text-[10px] font-bold flex items-center justify-center print:bg-ochre">{i + 1}</span>
                  <span className="text-ink">{pick(d, lang)}</span>
                </li>
              ))}
            </ol>
          </Block>

          <div className="grid grid-cols-2 gap-6 break-inside-avoid">
            <Block title={t("guide_dishes")}>
              <ul className="text-sm text-ink space-y-1">{g.dishes.map((d) => <li key={d.en}>• {pick(d, lang)}</li>)}</ul>
            </Block>
            {g.cuisine && Object.keys(g.cuisine).length > 0 && (
              <Block title={t("guide_cuisine")}>
                <ul className="text-sm text-ink space-y-1.5" data-testid="print-cuisine">
                  {["dish", "dessert", "street"].map((k) => g.cuisine[k] && (
                    <li key={k}><b>{pick(g.cuisine[k].name, lang)}</b> — {pick(g.cuisine[k].desc, lang)}<br /><span className="text-xs text-muted-foreground">{t("guide_ingredients")}: {(g.cuisine[k].ingredients[lang] || g.cuisine[k].ingredients.en).join(", ")}</span>
                      {g.cuisine[k].recipe && <> <Link to={`/recipe/${country.code}/${k}`} className="text-xs text-ochre hover:underline print:hidden" data-testid={`print-guide-recipe-${k}`}>→ {t("recipe_view")}</Link></>}
                    </li>
                  ))}
                </ul>
              </Block>
            )}
            <Block title={t("guide_best_time")}>
              <p className="text-sm text-ink">{pick(g.best_time, lang)}</p>
            </Block>
          </div>

          <Block title={t("guide_getting_around")}>
            <p className="text-sm text-ink/85">{pick(g.getting_around, lang)}</p>
          </Block>

          <Block title={t("guide_tip")}>
            <p className="text-sm text-ink/85 border-s-2 border-oasis ps-3">{pick(g.tip, lang)}</p>
          </Block>
        </div>
      )}

      <footer className="mt-8 pt-3 border-t border-stoneLine text-[10px] text-muted-foreground flex justify-between">
        <span>{t("photo_wiki")} · {t("source_wb")}</span>
        <span dir="ltr">{window.location.origin}/?c={country.code}</span>
      </footer>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <section className="break-inside-avoid">
      <h2 className="text-[10px] uppercase tracking-[0.18em] text-ochre mb-2 font-semibold">{title}</h2>
      {children}
    </section>
  );
}
